import { useCallback, useRef } from 'react';
import ICursorable from 'types/Cursorable';
import { useScrollBreakpoint } from '.';
import { ILazyLoadingState } from './useLazyLoading/useLazyLoading';
import usePagedCursorLoader, { IUsePagedCursorLoaderPayload } from './usePagedCursorLoader';

export default function useCursorScrollingLoader<T extends ICursorable>(
  scroller: HTMLElement | HTMLDivElement | Document | null | undefined,
  state: ILazyLoadingState<T>,
  getData: (after: string | null, first: number) => Promise<T[]>,
  sectionSize: number = 20,
  scrollOffset: number = 100,
  onLoad: (data: T[], loadedAll: boolean, error: boolean) => void
) {
  const isLoading = useRef(false);
  const inFlightLoad = useRef<Promise<IUsePagedCursorLoaderPayload<T>> | null>(null);
  const inFlightLoadAll = useRef<Promise<IUsePagedCursorLoaderPayload<T>> | null>(null);
  const { getMoreData, loadAll } = usePagedCursorLoader<T>(state, getData, sectionSize);

  // Run a single page load, tracking it so `loadAllData` can wait for any in-flight page load —
  // scroll-triggered or programmatic (including the initial fetch) — before loading everything.
  // Otherwise a late partial `onLoad` from that load could overwrite the fully-loaded result.
  const runPageLoad = useCallback(async () => {
    isLoading.current = true;
    const loadPromise = getMoreData();
    inFlightLoad.current = loadPromise;
    try {
      const payload = await loadPromise;
      onLoad(payload.data, payload.loadedAll, payload.error);
      return payload;
    } finally {
      isLoading.current = false;
      inFlightLoad.current = null;
    }
  }, [getMoreData, onLoad]);

  const onBreakpointReached = useCallback(async () => {
    if (!state.loading && !state.loadedAll && !isLoading.current) {
      await runPageLoad();
    }
  }, [state.loading, state.loadedAll, runPageLoad]);

  const loadAllData = useCallback(async () => {
    // Only short-circuit when everything is already loaded; that snapshot is complete. In every
    // other case (including an initial fetch still in flight) actually run the load so callers
    // (e.g. the export) never receive a partial set.
    if (state.loadedAll) {
      return { data: state.data, loadedAll: state.loadedAll, error: state.error };
    }

    // Coalesce concurrent loadAll calls (e.g. a double-click, or multiple callers) onto a single
    // in-flight run so we don't kick off redundant full-table fetch loops with racing onLoad
    // dispatches.
    if (inFlightLoadAll.current) {
      return inFlightLoadAll.current;
    }

    const loadEverything = async (): Promise<IUsePagedCursorLoaderPayload<T>> => {
      // Wait for any in-flight page load to finish first so its (partial) `onLoad` can't land after
      // ours and overwrite the fully-loaded result. Holding `isLoading` then prevents a new
      // scroll-triggered load from starting while we load everything.
      if (inFlightLoad.current) {
        await inFlightLoad.current.catch(() => undefined);
      }

      isLoading.current = true;
      try {
        const payload = await loadAll();
        onLoad(payload.data, payload.loadedAll, payload.error);
        return payload;
      } finally {
        isLoading.current = false;
      }
    };

    const loadAllPromise = loadEverything();
    inFlightLoadAll.current = loadAllPromise;
    try {
      return await loadAllPromise;
    } finally {
      inFlightLoadAll.current = null;
    }
  }, [loadAll, onLoad, state.data, state.loadedAll, state.error]);

  useScrollBreakpoint(scroller ?? document, onBreakpointReached, scrollOffset);

  return { getMoreData: runPageLoad, loadAll: loadAllData };
}
